import { useNavigate } from 'react-router';
import type { DashboardAttentionItem } from '../../types/dashboard';
import { timeAgo } from '../../utils/sessionTime';

interface AttentionPanelProps {
  items: DashboardAttentionItem[];
  loading?: boolean;
  empty?: boolean;
}

export function AttentionPanel({ items, loading = false }: AttentionPanelProps) {
  const navigate = useNavigate();

  const handleItemClick = (item: DashboardAttentionItem) => {
    if (item.kind === 'pending_confirmation' || item.kind === 'payment_failed') {
      if (item.refId) {
        navigate(`/sessions/${item.refId}`);
      }
    }
  };

  const hasItems = items.length > 0;
  const isInteractive = (kind: string) => kind === 'pending_confirmation' || kind === 'payment_failed';

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 hair-b flex items-center justify-between">
        <h3 className="text-[14px] font-semibold flex items-center gap-2">
          <span className={`dot ${hasItems ? 'bg-spark pulse-soft' : 'bg-sage'}`} /> Needs your attention
        </h3>
        {hasItems && <span className="text-[11px] text-ink-4">{items.length} items</span>}
      </div>

      {/* Items */}
      <div className="divide-hair">
        {loading && !hasItems ? (
          // Loading skeleton: 3 rows
          <>
            <div className="px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="h-3 w-40 bg-canvas animate-pulse rounded" />
                  <div className="h-3 w-32 bg-canvas animate-pulse rounded mt-2" />
                </div>
                <div className="h-3 w-16 bg-canvas animate-pulse rounded mt-0.5 shrink-0" />
              </div>
            </div>
            <div className="px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="h-3 w-40 bg-canvas animate-pulse rounded" />
                  <div className="h-3 w-32 bg-canvas animate-pulse rounded mt-2" />
                </div>
                <div className="h-3 w-16 bg-canvas animate-pulse rounded mt-0.5 shrink-0" />
              </div>
            </div>
            <div className="px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="h-3 w-40 bg-canvas animate-pulse rounded" />
                  <div className="h-3 w-32 bg-canvas animate-pulse rounded mt-2" />
                </div>
                <div className="h-3 w-16 bg-canvas animate-pulse rounded mt-0.5 shrink-0" />
              </div>
            </div>
          </>
        ) : !hasItems ? (
          // Empty state
          <div className="px-4 py-8 text-center">
            <p className="text-[13px] text-ink-3 font-medium flex items-center justify-center gap-1.5">
              <span className="dot bg-sage" />
              All caught up
            </p>
            <p className="text-[12px] text-ink-4 mt-1">
              Pending confirmations, payment issues, and new leads will appear here.
            </p>
          </div>
        ) : (
          // Items
          items.map((item) => (
            isInteractive(item.kind) ? (
              <a
                key={item.id}
                href="#"
                className="block px-4 py-3 hover:bg-paper-2 transition cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleItemClick(item);
                }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-medium">{item.title}</div>
                    <div className="text-[11px] text-ink-4 mt-0.5">{item.detail}</div>
                  </div>
                  <span className="text-[11px] text-ink-5 mt-0.5 shrink-0">{timeAgo(item.occurredAt)}</span>
                </div>
              </a>
            ) : (
              // new_lead: non-interactive
              <div key={item.id} className="block px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-medium">{item.title}</div>
                    <div className="text-[11px] text-ink-4 mt-0.5">{item.detail}</div>
                  </div>
                  <span className="text-[11px] text-ink-5 mt-0.5 shrink-0">{timeAgo(item.occurredAt)}</span>
                </div>
              </div>
            )
          ))
        )}
      </div>
    </div>
  );
}
